import React, { useEffect } from 'react'
import { Collapse, Box } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { useTranslation } from 'react-i18next'
import attributes from './attributes'
import RequestItemCard from './RequestItemCard'
import { CollapsibleFilterPanel } from 'packages/eid-ui'
import { AppLayout, useEidGrid, CardViewLoader } from 'components'
import { useIsSmallScreen, useQuery, useSessionStorage } from 'packages/core'
import { useHistory } from 'react-router'
import StickyContainer from 'components/StickyContainer'
import {
    RequestItemsTable,
    RequestItemsTableHead,
    RequestItemRow,
} from './RequestItemsTableComponents'
import {
    Filters,
    ListingGrid,
    GridContainer,
    FiltersColumn,
    FilterContainer,
    DesktopContainer,
    MobileFiltersScreen,
    SmallScreenContainer,
    MobileStickyContentListing,
} from 'components'
import DetailsDrawer from './DetailsDrawer'
import { SubPathLevel } from 'appConfigurations'

const useStyles = makeStyles(() => ({
    filtersearch: {
        width: '100%',
        maxWidth: '400px',
        '@media (max-width:1700px)': {
            maxWidth: '300px',
        },
    },
    datefiltersearchCollapse: {
        width: '100%',
        padding: '0px 5px',
        height: '40px',
        maxWidth: '100%',
    },
    datefiltersearch: {
        width: '100%',
        padding: '0px 5px',
        height: '40px',
    },
    filtermainsearch: {
        display: 'flex',
        width: '100%',
        position: 'relative',
        zIndex: 0,
    },
}))

const RequestItemsLayout = ({
    list,
    totalCount,
    isLoading,
    isLoadingMore,
    pagination,
    filters = [],
    refetch,
    type,
}) => {
    const { t } = useTranslation()
    const [openFilters, setOpenFilters] = useSessionStorage(
        'FILTERS_EXPANDED',
        true,
    )
    const isSmallScreen = useIsSmallScreen()
    const defaultView = isSmallScreen ? 'card' : 'table'
    const { viewSwitcher, setCurrentView, viewToRender } = useEidGrid({
        isLoading,
        data: list,
        isLoadingMore,
        noDataMessage: t('NoBusinessRequestItemsFound'),
        pagination,
        attributes,
        cardViewConfig: {
            LoadingIndicatorComponent: CardViewLoader,
            CardComponent: RequestItemCard,
        },
        tableViewConfig: {
            TableComponent: RequestItemsTable,
            TableHeadComponent: RequestItemsTableHead,
            TableRowComponent: RequestItemRow,
        },
        defaultView,
    })

    useEffect(() => {
        if (isSmallScreen) {
            setCurrentView('card')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSmallScreen])

    const query = useQuery()
    const id = query.get('id')
    const history = useHistory()

    const {
        location: { pathname },
    } = history
    const classes = useStyles()
    const areaName = pathname.split('/')[1 + SubPathLevel]
    const routeName = pathname.split('/')[2 + SubPathLevel]

    const showFilters = query.get('filters') === 'visible'
    const dateFilterOptions = [
        {
            label: t('RequestInitiated'),
            enumValue: 'Initiated',
            value: 'requestInitiated',
            default: true,
        },
        {
            label: t('RequestStatusChanged'),
            enumValue: 'StatusChanged',
            value: 'requestApproved',
        },
    ]

    const mobileFiltersScreen = (
        <MobileFiltersScreen
            totalCount={totalCount}
            filters={filters}
            handleApplyFilters={() => {
                query.delete('filters')
                history.push(`${history.location.pathname}?${query.toString()}`)
            }}
        />
    )

    const mobileStickyContentListing = (
        <MobileStickyContentListing
            totalCount={totalCount}
            title={areaName}
            subTitle={routeName}
            handleApplyFilters={() => {
                query.set('filters', 'visible')
                history.push(`${history.location.pathname}?${query.toString()}`)
            }}
        />
    )

    let content
    if (isSmallScreen) {
        content = (
            <SmallScreenContainer>
                <Box display={showFilters ? '' : 'none'}>
                    {mobileFiltersScreen}
                </Box>

                <Box display={!showFilters ? '' : 'none'}>
                    <Box display={!id ? '' : 'none'}>
                        <StickyContainer>
                            <Box width="100%" padding="12px 14px">
                                {mobileStickyContentListing}
                            </Box>
                        </StickyContainer>
                        {viewToRender}
                    </Box>
                </Box>
            </SmallScreenContainer>
        )
    } else {
        content = (
            <DesktopContainer>
                <FiltersColumn>
                <CollapsibleFilterPanel
                        openFilters={openFilters}
                        setOpenFilters={(e) => setOpenFilters((prev) => !prev)}
                    />
                    <Collapse in={openFilters} timeout={300} unmountOnExit>
                        <>
                            {filters.map((f) => {
                                const FilterToRender =
                                    Filters.filtersMap[f.name]
                                return (
                                    <FilterContainer
                                        data-protectedsubcomponent={
                                            f.requireAccess
                                        }
                                        key={f.name}
                                    >
                                        <FilterToRender />
                                    </FilterContainer>
                                )
                            })}
                        </>
                    </Collapse>
                </FiltersColumn>

                <Box width="100%">
                    <Box className={classes.filtermainsearch}>
                        <Box className={classes.filtersearch}>
                            <Filters.TextSearch />
                        </Box>
                        <Box
                            className={
                                openFilters
                                    ? classes.datefiltersearch
                                    : classes.datefiltersearchCollapse
                            }
                        >
                            <Filters.DateFilter options={dateFilterOptions} />
                        </Box>
                    </Box>

                    <ListingGrid>
                        <GridContainer openFilters={openFilters}>
                            {viewToRender}
                        </GridContainer>
                    </ListingGrid>
                </Box>
            </DesktopContainer>
        )
    }

    return (
        <AppLayout totalCount={totalCount} viewSwitcher={viewSwitcher}>
            {id && (
                <DetailsDrawer
                    id={id}
                    onClose={() => {
                        history.push(history.location.pathname)
                        refetch()
                    }}
                />
            )}

            {content}
        </AppLayout>
    )
}

export default RequestItemsLayout
